/**
 * Simple phone number utilities for WhatsApp Evolution API
 */

/**
 * Normalizes a phone number for Evolution API
 * Adds country code +51 for Peru if missing and ensures + prefix
 *
 * @param phoneNumber - Phone number in various formats
 * @returns Normalized phone number with + prefix
 *
 * Examples:
 * - "987654321" -> "+51987654321"
 * - "51987654321" -> "+51987654321"
 * - "+51987654321" -> "+51987654321"
 */
export function normalizePhoneForEvolution(phoneNumber: string): string {
  if (!phoneNumber) {
    throw new Error('Phone number is required');
  }

  // Remove all non-numeric characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // Remove + if exists, we'll add it back
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }

  // Handle Peru specific cases
  if (cleaned.length === 9) {
    // Local number, add Peru country code
    return `+51${cleaned}`;
  }

  if (cleaned.length === 11 && cleaned.startsWith('51')) {
    // Already has country code
    return `+${cleaned}`;
  }

  if (cleaned.length === 12 && cleaned.startsWith('051')) {
    // Remove leading 0
    return `+${cleaned.substring(1)}`;
  }

  // For other cases, assume it needs + prefix
  if (!cleaned.startsWith('+')) {
    return `+${cleaned}`;
  }

  return cleaned;
}

/**
 * Validates if a phone number is valid for Peru
 */
export function isValidPeruvianPhone(phoneNumber: string): boolean {
  try {
    const normalized = normalizePhoneForEvolution(phoneNumber);
    // Peru numbers should be +51 followed by 9 digits starting with 9
    return /^\+51[9]\d{8}$/.test(normalized);
  } catch {
    return false;
  }
}
